import React, { useEffect, useRef } from 'react';

// Google Maps component for delivery route visualization
const DeliveryMap = ({ simulationResult, pickupPlace, dropoffs, pickupName }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const markersRef = useRef([]);
  const legLabelMarkersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    // Initialize map
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      zoom: 12,
      center: { lat: 6.1725, lng: 1.2314 }, // Default to Cotonou
      mapId: 'DEMO_MAP_ID', // required for AdvancedMarkerElement
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        },
        {
          featureType: 'transit',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      zoomControl: false,
      keyboardShortcuts: false,
      cameraControl: false
    });

    directionsServiceRef.current = new window.google.maps.DirectionsService();
    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#7C3AED', // violet
        strokeWeight: 4,
        strokeOpacity: 0.85
      }
    });

    directionsRendererRef.current.setMap(mapInstanceRef.current);
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !simulationResult || !pickupPlace) return;

    // Clear existing markers
    markersRef.current.forEach(marker => { marker.map = null; });
    markersRef.current = [];
    legLabelMarkersRef.current.forEach(marker => { marker.map = null; });
    legLabelMarkersRef.current = [];

    const deliveries = simulationResult.groups?.[0]?.deliveries || [];
    if (deliveries.length === 0) return;

    // Create pickup marker
    const pickupEl = document.createElement('div');
    pickupEl.style.cssText = 'width:28px;height:28px;border-radius:50%;background:#10B981;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)';
    const pickupMarker = new window.google.maps.marker.AdvancedMarkerElement({
      position: { lat: pickupPlace.lat, lng: pickupPlace.lng },
      map: mapInstanceRef.current,
      title: pickupName || 'Point de ramassage',
      content: pickupEl
    });
    markersRef.current.push(pickupMarker);

    // Create delivery markers
    deliveries.forEach((delivery, index) => {
      const deliveryName =
        // dropoffs?.[delivery.routePosition - 1]?.value ||
        delivery.referenceId ||
        delivery.address ||
        `Livraison ${delivery.routePosition}`;

      const deliveryEl = document.createElement('div');
      deliveryEl.style.cssText = 'width:32px;height:32px;border-radius:50%;background:#0F172A;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;color:white;font-weight:bold;font-size:12px;font-family:Arial,sans-serif';
      deliveryEl.textContent = delivery.routePosition.toString();
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat: delivery.dlat, lng: delivery.dlng },
        map: mapInstanceRef.current,
        title: deliveryName,
        content: deliveryEl
      });
      markersRef.current.push(marker);
    });

    const formatLegMinutes = (seconds) => {
      if (seconds === undefined || seconds === null) return '';
      const mins = Math.round(seconds / 60);
      return `${mins} min`;
    };

    const createDurationElement = (text) => {
      const div = document.createElement('div');
      div.style.cssText = 'background:white;border-radius:14px;padding:4px 12px;font-size:12px;color:#111827;font-family:Arial,sans-serif;box-shadow:0 2px 6px rgba(0,0,0,0.2);white-space:nowrap;pointer-events:none';
      div.textContent = text;
      return div;
    };

    // Calculate and display route
    if (deliveries.length > 0) {
      const ordered = deliveries.slice().sort((a, b) => a.routePosition - b.routePosition);
      const waypoints = ordered.slice(0, -1).map(delivery => ({
        location: { lat: delivery.dlat, lng: delivery.dlng },
        stopover: true
      }));

      const lastDelivery = ordered[ordered.length - 1];

      directionsServiceRef.current.route(
        {
          origin: { lat: pickupPlace.lat, lng: pickupPlace.lng },
          destination: { lat: lastDelivery.dlat, lng: lastDelivery.dlng },
          waypoints: waypoints,
          optimizeWaypoints: false,
          travelMode: window.google.maps.TravelMode.DRIVING
        },
        (result, status) => {
          if (status === 'OK') {
            directionsRendererRef.current.setDirections(result);

            // Add duration labels on each leg using API data
            const legs = result.routes?.[0]?.legs || [];
            legs.forEach((leg, index) => {
              // Use API duration data instead of Google's calculated duration
              const apiDelivery = ordered[index];
              const legDurationText = apiDelivery ? `${apiDelivery.duration} min` : formatLegMinutes(leg.duration?.value);
              if (!legDurationText) return;
              const midLat = (leg.start_location.lat() + leg.end_location.lat()) / 2;
              const midLng = (leg.start_location.lng() + leg.end_location.lng()) / 2;
              const legMarker = new window.google.maps.marker.AdvancedMarkerElement({
                position: { lat: midLat, lng: midLng },
                map: mapInstanceRef.current,
                content: createDurationElement(legDurationText)
              });
              legLabelMarkersRef.current.push(legMarker);
            });
          }
        }
      );
    }

    // Fit map to show all markers
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend({ lat: pickupPlace.lat, lng: pickupPlace.lng });
    deliveries.forEach(delivery => {
      bounds.extend({ lat: delivery.dlat, lng: delivery.dlng });
    });
    mapInstanceRef.current.fitBounds(bounds);
  }, [simulationResult, pickupPlace, dropoffs, pickupName]);

  return (
    <div className="w-full h-full rounded-2xl overflow-hidden shadow-lg">
      <div ref={mapRef} className="w-full h-full min-h-[400px]" />
    </div>
  );
};

export default DeliveryMap;
